import { useCallback, useEffect, useState } from "react";

import { listSmileSimulationRecords } from "../api/smileSimulation.records";
import type { SmileSimulationRecordViewModel } from "../model/view-model";

type SmileSimulationRecordsState = {
  records: SmileSimulationRecordViewModel[];
  isLoading: boolean;
  errorMessage: string | null;
};

const initialSmileSimulationRecordsState: SmileSimulationRecordsState = {
  records: [],
  isLoading: false,
  errorMessage: null,
};

const getErrorMessage = (error: unknown) =>
  error instanceof Error
    ? error.message
    : "환자 기록을 불러오지 못했어요. 다시 시도해 주세요.";

export function useSmileSimulationRecords(clinicId: string | null) {
  const [state, setState] = useState<SmileSimulationRecordsState>(
    initialSmileSimulationRecordsState,
  );

  const refresh = useCallback(async () => {
    if (!clinicId) {
      setState(initialSmileSimulationRecordsState);
      return [];
    }

    setState((currentState) => ({
      ...currentState,
      isLoading: true,
      errorMessage: null,
    }));

    try {
      const records = await listSmileSimulationRecords(clinicId);

      setState({
        records,
        isLoading: false,
        errorMessage: null,
      });

      return records;
    } catch (error) {
      const errorMessage = getErrorMessage(error);

      console.error("Failed to load smile simulation records:", error);
      setState((currentState) => ({
        ...currentState,
        isLoading: false,
        errorMessage,
      }));

      throw error;
    }
  }, [clinicId]);

  useEffect(() => {
    let active = true;

    if (!clinicId) {
      setState(initialSmileSimulationRecordsState);
      return () => {
        active = false;
      };
    }

    setState((currentState) => ({
      ...currentState,
      isLoading: true,
      errorMessage: null,
    }));

    void listSmileSimulationRecords(clinicId)
      .then((records) => {
        if (!active) {
          return;
        }

        setState({
          records,
          isLoading: false,
          errorMessage: null,
        });
      })
      .catch((error: unknown) => {
        if (!active) {
          return;
        }

        console.error("Failed to load smile simulation records:", error);
        setState((currentState) => ({
          ...currentState,
          isLoading: false,
          errorMessage: getErrorMessage(error),
        }));
      });

    return () => {
      active = false;
    };
  }, [clinicId]);

  const upsertRecord = useCallback((record: SmileSimulationRecordViewModel) => {
    setState((currentState) => ({
      ...currentState,
      records: [
        record,
        ...currentState.records.filter((item) => item.id !== record.id),
      ].sort(
        (left, right) =>
          new Date(right.createdAt).getTime() -
          new Date(left.createdAt).getTime(),
      ),
    }));
  }, []);

  return {
    errorMessage: state.errorMessage,
    isLoading: state.isLoading,
    records: state.records,
    refresh,
    upsertRecord,
  };
}
